import React, { useState } from "react";
import * as Mui from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Search from "@mui/icons-material/Search";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBox from "@mui/icons-material/CheckBox";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors } from "services/constants/designTokens";
import { roleAndGroupUi } from "../ui";

export const DEPARTMENT_SCOPES = [
  { value: "all", label: "All Departments" },
  { value: "current", label: "Current Department" },
  { value: "managed", label: "Managed Departments" },
  { value: "additional", label: "Additional Departments" },
];
export const hasValidDepartmentScope = (row) =>
  DEPARTMENT_SCOPES.some(({ value }) => value === row.scope) &&
  (row.scope !== "additional" || row.departments?.length > 0);
export const matchesDefaultScope = (row, defaults) =>
  row.scope === defaults.scope && (row.scope !== "additional" ||
    (row.departments?.length === defaults.departments.length &&
      row.departments.every((department) => defaults.departments.some((item) => String(item.id) === String(department.id)))));

// The API expects the human-readable scope label (e.g. "All Departments"),
// not the internal value ("all") used to drive the UI.
export const getDepartmentScopeLabel = (value) =>
  DEPARTMENT_SCOPES.find((item) => item.value === value)?.label || value;

// Reverse of getDepartmentScopeLabel: normalizes a scope coming back from
// the API into the internal value ("all") this component and its callers
// key off of. The GET returns the short value ("all", confirmed live
// 2026-10-01) even though the label ("All Departments") is what is sent, so
// both are accepted, case-insensitively. Matching labels only used to turn
// "all" into undefined, which the form then showed as Additional
// Departments listing every department.
export const getDepartmentScopeValue = (scope) => {
  const key = String(scope ?? "").trim().toLowerCase();
  if (!key) return undefined;
  return DEPARTMENT_SCOPES.find(
    (item) => item.value === key || item.label.toLowerCase() === key,
  )?.value;
};

// Formats an editor scope. userGroupPayload selects id/scope/departments
// for the latest create/edit API, excluding the legacy company field.
// Departments are included only for "Additional Departments".
export const buildUserScopePayload = (user) => ({
  id: user.id,
  company_id: user.company_id,
  scope: getDepartmentScopeLabel(user.scope),
  ...(user.scope === "additional"
    ? { departments: (user.departments || []).map((dept) => dept.id) }
    : {}),
});

// Builds one `assignments[]` entry for the create/edit user-group payload:
// the API only needs the ids, not the display names carried alongside them
// in local state for rendering the Companies & Role Assignment table.
export const buildAssignmentPayload = (assignment) => ({
  company_id: assignment.company_id,
  role_id: assignment.role_id,
});

const departmentName = (item) => item.department || item.department_name || item.name || "";

// showDepartmentNames: for the "Additional Departments" scope, label the
// button with the chosen department names (e.g. "HR, Finance") instead of
// the scope name -- used by the User Group edit panel.
export const DepartmentMultiSelect = ({ options = [], value = [], scope = "additional", onChange, badge, label = "Department scope", showDepartmentNames = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftScope, setDraftScope] = useState(scope);
  const [draftDepartments, setDraftDepartments] = useState(value);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);
  const filteredOptions = (Array.isArray(options) ? options : []).filter((item) =>
    departmentName(item).toLowerCase().includes(search.trim().toLowerCase()));
  const departmentNames = (value || []).map(departmentName).filter(Boolean);
  const showNames = showDepartmentNames && scope === "additional" && departmentNames.length > 0;
  const scopeLabel = showNames
    ? departmentNames[0]
    : DEPARTMENT_SCOPES.find((item) => item.value === scope)?.label || "Select scope";
  // With department names shown, only the first is written out; the rest
  // fold into a "+N more" chip and the styled tooltip lists them all.
  const moreCount = showNames ? departmentNames.length - 1 : 0;
  const toggleDepartment = (department) => setDraftDepartments((previous) =>
    previous.some((item) => String(item.id) === String(department.id))
      ? previous.filter((item) => String(item.id) !== String(department.id)) : [...previous, department]);
  const trigger = (
    <Mui.Button variant="outlined" fullWidth aria-label={label} aria-haspopup="dialog" aria-expanded={open}
      onClick={(event) => { setDraftScope(scope); setDraftDepartments((value || []).filter((department) => options.some((option) => String(option.id) === String(department.id)))); setSearch(""); setAnchorEl(event.currentTarget); }}
      endIcon={open ? <ExpandLess /> : <ExpandMore />}
      sx={{ minHeight: 40, px: 1, borderColor: open ? "#2563EB" : "#D7E0EA", borderRadius: "6px", color: "#0F172A", textTransform: "none", justifyContent: "space-between", fontSize: 12, textAlign: "left", backgroundColor: "#FFFFFF" }}>
      <Mui.Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
        <span style={showDepartmentNames ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 } : undefined}>{scopeLabel}{!showDepartmentNames && scope === "additional" && value.length ? ` (${value.length})` : ""}</span>
        {moreCount > 0 && <Mui.Chip component="span" label={`+${moreCount} more`} size="small" sx={{ flexShrink: 0, height: 22, fontSize: 11.5, fontWeight: 600, color: designColors.tealDark, backgroundColor: designColors.tealTint, border: "1px solid #CDEDE4", borderRadius: "6px", cursor: "inherit", "& .MuiChip-label": { px: 0.75 } }} />}
      </Mui.Box>
      {badge && <Mui.Chip label={badge} size="small" sx={{ ml: 0.5, height: 19, fontSize: 10, color: badge === "Default" ? "#2563EB" : "#C2410C", backgroundColor: badge === "Default" ? "#EFF6FF" : "#FFF7ED" }} />}
    </Mui.Button>
  );
  return <>
    {/* Always wrapped (title cleared when not needed) so opening the
        popover never remounts the button it is anchored to. */}
    <Mui.Tooltip arrow slotProps={tooltipStyleProps} placement="top"
      title={moreCount > 0 && !open ? departmentNames.join(", ") : ""}>{trigger}</Mui.Tooltip>
    <Mui.Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ role: "dialog", "aria-label": label, sx: { width: 290, maxWidth: "calc(100vw - 32px)", p: 1.5, border: "1px solid #D7E0EA", borderRadius: "6px" } }}>
      <Mui.RadioGroup value={draftScope} onChange={(event) => setDraftScope(event.target.value)} aria-label="Scope options">
        {DEPARTMENT_SCOPES.map((item) => <Mui.FormControlLabel key={item.value} value={item.value}
          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13, flex: 1 } }}
          control={<Mui.Radio icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} size="small" sx={{ p: 0.5, mr: 0.5, "&.Mui-checked": { color: designColors.teal } }} />}
          label={<Mui.Stack direction="row" justifyContent="space-between" alignItems="center">{item.label}{item.value === "additional" && (draftScope === "additional" ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}</Mui.Stack>} />)}
      </Mui.RadioGroup>
      {draftScope === "additional" && <Mui.Box sx={{ mt: 1, border: "1px solid #D7E0EA", borderRadius: "5px" }}>
        <Mui.TextField autoFocus fullWidth size="small" placeholder="Search departments..." value={search}
          onChange={(event) => setSearch(event.target.value)} inputProps={{ "aria-label": "Search departments" }}
          InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search fontSize="small" /></Mui.InputAdornment> }} />
        <Mui.Box sx={{ maxHeight: 210, overflowY: "auto", p: 0.5 }}>
          {filteredOptions.map((department) => {
            const checked = draftDepartments.some((item) => String(item.id) === String(department.id));
            return <Mui.FormControlLabel key={department.id} sx={{ display: "flex", m: 0, borderRadius: 1, backgroundColor: checked ? "#E6F8F3" : "transparent", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
              control={<Mui.Checkbox size="small" checked={checked} onChange={() => toggleDepartment(department)} sx={{ p: 0.5, mr: 0.5, "&.Mui-checked": { color: designColors.teal } }} />}
              label={departmentName(department)} />;
          })}
          {!filteredOptions.length && <Mui.Typography sx={{ p: 1, fontSize: 13, color: "text.secondary" }}>No departments found</Mui.Typography>}
        </Mui.Box>
      </Mui.Box>}
      <Mui.Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1 }}>
        <Mui.Typography sx={{ fontSize: 11, color: "#64748B" }}>{draftScope === "additional" ? `${draftDepartments.length} departments selected` : ""}</Mui.Typography>
        {draftScope === "additional" && <Mui.Button size="small" onClick={() => setDraftDepartments([])} sx={{ minWidth: 0, color: designColors.teal, textTransform: "none", textDecoration: "underline" }}>Clear</Mui.Button>}
        <Mui.Button size="small" variant="contained" disabled={draftScope === "additional" && !draftDepartments.length}
          onClick={() => { onChange(draftScope === "additional" ? draftDepartments : [], draftScope); setAnchorEl(null); }}
          sx={{
            ml: "auto",
            backgroundColor: "#009688",
            textTransform: "none",
            boxShadow: "none",
            // Without an explicit hover the theme's blue primary showed through.
            "&:hover": { backgroundColor: designColors.tealDark, boxShadow: "none" },
          }}>Done</Mui.Button>
      </Mui.Stack>
    </Mui.Popover>
  </>;
};

export const DefaultDepartmentScope = ({ options, value, onChange, onApply }) => (
  <Mui.Box>
    <Mui.Box sx={{ ...roleAndGroupUi.cardHeaderTitleRow, mb: 0.75 }}>
      <Mui.Typography sx={{ fontSize: 13 }}>Default Scope for this Group</Mui.Typography>
      <Mui.Tooltip
        arrow
        slotProps={tooltipStyleProps}
        title="New users added to this group will use the default scope. You can override the scope for individual users below."
      >
        <InfoOutlined sx={roleAndGroupUi.infoIcon} />
      </Mui.Tooltip>
    </Mui.Box>
    <Mui.Stack direction="row" spacing={1}>
      <Mui.Box sx={{ width: 225 }}><DepartmentMultiSelect options={options} value={value.departments} scope={value.scope} label="Default scope for this group" onChange={(departments, scope) => onChange({ departments, scope })} /></Mui.Box>
      <Mui.Button variant="outlined" onClick={onApply} sx={{ textTransform: "none", whiteSpace: "nowrap", color: "#00897B", borderColor: "#00897B", fontSize: 12 }}>Apply to all users</Mui.Button>
    </Mui.Stack>
  </Mui.Box>
);
